import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import * as BioJSUtil from 'biojs/Util';
import {Strand} from 'constants/MolbioConstants';
import {getActionButtonRoleProps} from 'lib/a11y/ButtonA11y';
import * as sequtil from 'molbio/SeqUtil';
import {FONT_PROXIMA_TITLE} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {BLUE, BLUE_LIGHT} from 'ui-library/Colors';

export interface CodonOptimizationTabSelectionHeaderProps {
  optimizationStart: number;
  optimizationEnd: number;
  seqLength: number;
  startIndex: number;
  onSetSelection: (start: number, end: number) => void;
  bases: string;
  strand: Strand;
  isOriginalSelection: boolean;
  isBackTranslation: boolean;
}

export const ORF_WARNING_TEXT = 'Region is not an ORF with a standard start codon';

export default function CodonOptimizationTabSelectionHeader(
  props: CodonOptimizationTabSelectionHeaderProps
): JSX.Element {
  const [selectionDisplayStart, selectionDisplayEnd] = sequtil.modelRangeToDisplay(
    props.optimizationStart,
    props.optimizationEnd,
    props.seqLength,
    props.startIndex
  );
  const strand = props.strand === 1 ? ' (Forward Strand)' : props.strand === -1 ? ' (Reverse Strand)' : '';
  const shouldShowORFWarning = !props.isBackTranslation && !BioJSUtil.isOrf(props.bases);
  const resetSelection = (): void => {
    props.onSetSelection(props.optimizationStart, props.optimizationEnd);
  };
  return (
    <div {...getTestProps({component: 'CodonOptimizationTabSelectionHeader', element: 'root'})}>
      <div className={css(styles.header)}>
        <h3 className={css(styles.title)}>Codon optimization</h3>
        <span
          className={css(styles.selectionText)}
          {...getActionButtonRoleProps({onPress: resetSelection})}
          {...getTestProps({component: 'CodonOptimizationTabSelectionHeader', element: 'selectionText'})}
        >{`Region: ${selectionDisplayStart}-${selectionDisplayEnd}${strand}`}</span>
      </div>
      {shouldShowORFWarning && (
        <div className={css(styles.bannerContainer)}>
          <InlineBanner bannerType={InlineBannerType.WARNING} text={ORF_WARNING_TEXT} />
        </div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  title: {
    margin: 0,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  selectionText: {
    color: BLUE_LIGHT,
    ':hover': {
      color: BLUE,
    },
    cursor: 'pointer',
    ...FONT_PROXIMA_TITLE,
  },
  bannerContainer: {
    marginTop: 16,
  },
});
